/** Mobile cards for open positions and pending orders. */
import { useState, type ReactNode } from 'react';
import { closePrice, D, fmtPrice, fmtSigned, fmtUsd, fmtUsdSigned, orderTypeLabel, pairLabel, pnl, usdPerPoint, type Order, type Position, type Side } from '@platform/shared';
import { api } from '../../lib/api';
import { Link } from '../../lib/router';
import { useInstrument, useTicker } from '../../stores/market';
import { useAction } from '../BottomTabs';
import { InstrumentIcon } from '../InstrumentIcon';
import { cx, fmtLots, sideLabel, Spinner, TradeId, unitLabel } from '../ui';
import { NumInput } from '../NumInput';

const DEC_RE = /^\d*\.?\d*$/;
const signCls = (v: D) => (v.isNeg() ? 'text-down' : v.isPos() ? 'text-up' : '');

function Stat({ label, children, className, title }: { label: string; children: ReactNode; className?: string; title?: string }) {
  return (
    <div className="min-w-0" title={title}>
      <dt className="text-[13px] text-muted">{label}</dt>
      <dd className={cx('tnum m-0 mt-0.5 truncate text-[14px] font-semibold', className)}>{children}</dd>
    </div>
  );
}

export function SidePill({ side, leverage }: { side: Side; leverage: number }) {
  return (
    <span className={cx('shrink-0 rounded-[7px] px-2 py-[3px] text-[13px] font-semibold', side === 'long' ? 'bg-up-bg text-up' : 'bg-down-bg text-down')}>
      {sideLabel(side)} {leverage}x
    </span>
  );
}

/** Card header right side: the trade number, then the side pill. */
function CardTag({ tradeId, side, leverage }: { tradeId: number | null; side: Side; leverage: number }) {
  return (
    <span className="flex shrink-0 items-center gap-2">
      {tradeId != null && <TradeId id={tradeId} />}
      <SidePill side={side} leverage={leverage} />
    </span>
  );
}

function SymbolLink({ symbol, sub }: { symbol: string; sub: ReactNode }) {
  return (
    <Link to={`/trade/${symbol}`} className="flex min-w-0 items-center gap-2.5 text-fg no-underline">
      <InstrumentIcon symbol={symbol} size={32} />
      <span className="min-w-0">
        <span className="block truncate text-[17px] leading-tight font-semibold">{pairLabel(symbol)}</span>
        <span className="block truncate text-[13px] text-muted">{sub}</span>
      </span>
    </Link>
  );
}

function TpSlForm({ p, dp, onDone }: { p: Position; dp: number; onDone: () => void }) {
  const [tp, setTp] = useState(p.tpPrice ? D.from(p.tpPrice).toFixed(dp) : '');
  const [sl, setSl] = useState(p.slPrice ? D.from(p.slPrice).toFixed(dp) : '');
  const { err, busy, run } = useAction();
  const input = 'tnum mt-1 h-11 w-full min-w-0 rounded-xl border border-line-strong bg-bg px-3 text-[16px] font-semibold text-fg outline-none focus:border-fg placeholder:font-normal placeholder:text-muted';
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void run('save', async () => {
          await api.patch(`/api/positions/${p.id}`, { tpPrice: tp || null, slPrice: sl || null });
          onDone();
        });
      }}
      className="mt-3 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-end gap-2"
    >
      <label className="block text-[13px] font-semibold text-up">
        Take profit
        <NumInput value={tp} onValue={(v) => DEC_RE.test(v) && setTp(v)} placeholder="None" className={input} />
      </label>
      <label className="block text-[13px] font-semibold text-down">
        Stop loss
        <NumInput value={sl} onValue={(v) => DEC_RE.test(v) && setSl(v)} placeholder="None" className={input} />
      </label>
      <button type="submit" disabled={busy !== null} className="h-11 rounded-full bg-fg px-4 text-[15px] font-semibold text-bg disabled:opacity-50">
        {busy ? <Spinner /> : 'Save'}
      </button>
      {err && (
        <p role="alert" className="col-span-3 m-0 text-[13px] text-down">
          {err}
        </p>
      )}
    </form>
  );
}

/** One open position: live PnL, Close, and inline TP / SL editing. `titled` drops the symbol (used on that symbol's own screen). */
export function PositionCard({ p, titled }: { p: Position; titled?: boolean }) {
  const inst = useInstrument(p.symbol);
  const t = useTicker(p.symbol);
  const { err, busy, run } = useAction();
  const [editing, setEditing] = useState(false);
  const dp = inst?.priceDecimals ?? 2;
  const ccy = inst?.priceCcy ?? 'USD';
  const exit = t ? closePrice(p.side, { bid: D.from(t.bid), ask: D.from(t.ask) }) : null;
  const upnl = exit ? pnl(p.side, p.entryPrice, exit, p.qty, usdPerPoint(inst?.contractSize ?? '1', t?.fx)).add(p.swap) : null;
  const roe = upnl && D.from(p.margin).isPos() ? upnl.div(p.margin).mul(100) : null;
  return (
    <section aria-label={`${sideLabel(p.side)} ${pairLabel(p.symbol)} position`} className="rounded-[20px] border border-line bg-panel p-4">
      <div className="flex items-center justify-between gap-3">
        {titled ? <h3 className="m-0 text-[15px] font-semibold">{p.managedBy ? 'Managed position' : 'Your position'}</h3> : <SymbolLink symbol={p.symbol} sub={inst?.name ?? ''} />}
        <CardTag tradeId={p.tradeId} side={p.side} leverage={p.leverage} />
      </div>
      <div className="mt-2.5 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className={cx('tnum text-[24px] leading-[30px] font-semibold', upnl && signCls(upnl))}>{upnl ? fmtUsdSigned(upnl) : '—'}</div>
          <div className="tnum text-[14px] text-muted">{roe ? `${fmtSigned(roe, 1)}% return on margin` : 'Unrealized PnL'}</div>
        </div>
        {p.managedBy ? (
          <span title="Opened by our dealing team on your managed account" className="max-w-[55%] shrink-0 rounded-[14px] bg-managed-bg px-3 py-1 text-center text-[13px] leading-5 font-semibold break-words text-managed">
            {p.managedBy.name}
          </span>
        ) : (
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => void run('close', () => api.post(`/api/positions/${p.id}/close`))}
            className="h-10 shrink-0 rounded-full border border-line-strong bg-bg px-[18px] text-[15px] font-semibold text-fg disabled:opacity-50"
          >
            {busy === 'close' ? <Spinner /> : 'Close'}
          </button>
        )}
      </div>
      <dl className="m-0 mt-3.5 grid grid-cols-3 gap-2">
        <Stat label="Lots" title={`${p.qty} ${unitLabel(inst?.unit ?? '', p.qty)}`}>
          {fmtLots(p.qty, inst?.lotSize ?? '1')}
        </Stat>
        <Stat label="Entry">{fmtPrice(p.entryPrice, dp, ccy)}</Stat>
        <Stat label="Mark">{t ? fmtPrice(t.mark, dp, ccy) : '—'}</Stat>
      </dl>
      <div className="mt-3 flex items-center justify-between gap-3 border-t border-line pt-3 text-[14px]">
        <span className="tnum min-w-0 truncate text-muted">
          TP <span className={p.tpPrice ? 'text-up' : ''}>{p.tpPrice ? fmtPrice(p.tpPrice, dp, ccy) : '—'}</span> · SL{' '}
          <span className={p.slPrice ? 'text-down' : ''}>{p.slPrice ? fmtPrice(p.slPrice, dp, ccy) : '—'}</span>
        </span>
        {!p.managedBy && (
          <button type="button" aria-expanded={editing} onClick={() => setEditing((e) => !e)} className="shrink-0 font-semibold text-accent-fg">
            {editing ? 'Cancel' : 'Edit TP / SL'}
          </button>
        )}
      </div>
      {editing && <TpSlForm p={p} dp={dp} onDone={() => setEditing(false)} />}
      {err && (
        <p role="alert" className="m-0 mt-2 text-[13px] text-down">
          {err}
        </p>
      )}
    </section>
  );
}

export function OrderCard({ o }: { o: Order }) {
  const inst = useInstrument(o.symbol);
  const t = useTicker(o.symbol);
  const { err, busy, run } = useAction();
  const dp = inst?.priceDecimals ?? 2;
  const ccy = inst?.priceCcy ?? 'USD';
  const long = o.side === 'long';
  return (
    <section aria-label={`${orderTypeLabel(o.side, o.type)} order for ${pairLabel(o.symbol)}`} className="rounded-[20px] border border-line bg-panel p-4">
      <div className="flex items-center justify-between gap-3">
        <SymbolLink symbol={o.symbol} sub={<span>{orderTypeLabel(o.side, o.type)} order</span>} />
        <CardTag tradeId={o.tradeId} side={o.side} leverage={o.leverage} />
      </div>
      <dl className="m-0 mt-3.5 grid grid-cols-3 gap-2">
        {o.lots ? <Stat label="Lots">{o.lots}</Stat> : <Stat label="Value">{fmtUsd(o.notional)}</Stat>}
        <Stat label={o.type === 'stop' ? 'Stop price' : 'Limit price'}>{o.limitPrice ? fmtPrice(o.limitPrice, dp, ccy) : 'Market'}</Stat>
        <Stat label={long ? 'Ask now' : 'Bid now'}>{t ? fmtPrice(long ? t.ask : t.bid, dp, ccy) : '—'}</Stat>
      </dl>
      <div className="mt-3 flex items-center justify-between gap-3 border-t border-line pt-3 text-[14px]">
        <span className="tnum min-w-0 truncate text-muted">
          TP <span className={o.tpPrice ? 'text-up' : ''}>{o.tpPrice ? fmtPrice(o.tpPrice, dp, ccy) : '—'}</span> · SL{' '}
          <span className={o.slPrice ? 'text-down' : ''}>{o.slPrice ? fmtPrice(o.slPrice, dp, ccy) : '—'}</span>
        </span>
        <button type="button" disabled={busy !== null} onClick={() => void run('cancel', () => api.del(`/api/orders/${o.id}`))} className="shrink-0 font-semibold text-down disabled:opacity-50">
          {busy ? <Spinner /> : 'Cancel order'}
        </button>
      </div>
      {err && (
        <p role="alert" className="m-0 mt-2 text-[13px] text-down">
          {err}
        </p>
      )}
    </section>
  );
}
